﻿import React, { useState, useEffect } from 'react';
import { IonChromatographyIonDatum } from '../models/ion-chromatography-ion-datum';
import { UOM } from '../models/uom';
import { LookupItem } from '../models/lookup-item';
import IonChromatographyIonDataComponent from './IonChromatographyIonDataComponent';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import AuditHistoryComponent from './AuditHistoryComponent';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType, Entities } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';

import DataGrid, {
    Column,
    Selection,
    Paging,
    ColumnFixing,
    SearchPanel,
    Toolbar,
    Button as ColumnButton,
    Item as ToolBarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

let key = 0;

export interface Props {
    benchSheetId?: string;
    isReadOnly: boolean;
    datagridHeight?: string;
}

export default function IonChromatographyIonDataDataComponent(props: Props) {
    const [ionData, setIonData] =
        useState<IonChromatographyIonDatum[]>();

    const [selectedIonDatum, setSelectedIonDatum] =
        useState<IonChromatographyIonDatum>();

    const [ionDataDialogVisible, setIonDataDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.benchSheetId]);

    key++;

    return (
        <div className="pw-content">
            <div className="pw-datagrid">
                <DataGrid
                    height={props.datagridHeight ? props.datagridHeight : "100%"}
                    keyExpr="ionChromatographyDataId"
                    onSelectionChanged={onIonDataSelectionChanged}
                    wordWrapEnabled={true}
                    width="100%"
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    dataSource={ionData ? ionData : []}
                    showBorders={true}>

                    <SearchPanel visible={true} />
                    <LoadPanel enabled={true} />
                    <Selection mode='single' />
                    <Paging enabled={false} />

                    <Column type="buttons" width='5rem' >
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={showAuditInformationDialog} />
                        <ColumnButton hint="information" icon="info"
                            onClick={showIonDatumInformationButtonClicked} />
                    </Column>

                    <Column dataField='elution' dataType="number" width='8rem' alignment='center' />
                    <Column dataField='analyte' />
                    <Column dataField='resultString' dataType="string" caption="Result" width='12rem' />
                    <Column dataField='retentionTime' dataType="number" format={decimalFormat} width='12rem' />

                    <Toolbar>
                        <ToolBarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Ion Data</div>
                            </div>
                        </ToolBarItem>

                        <ToolBarItem location="after">
                            <Button hint="Edit" icon="inserttable" disabled={props.isReadOnly}
                                width="1.8rem" onClick={showIonDataDialog} />
                        </ToolBarItem>

                    </Toolbar>

                </DataGrid>
            </div>

            {ionDataDialogVisible &&
                <DialogPopup
                    visible={ionDataDialogVisible}
                    onHiding={hideIonDataDialog}
                    fullScreen={false}
                    hideOnOutsideClick={false}
                    dragEnabled={false}
                    width="60rem" height="32rem"
                    maxWidth="80vw" maxHeight="80vh"
                    title="Ion Data">
                    <IonChromatographyIonDataComponent benchSheetId={props.benchSheetId}
                        onClose={onCloseIonDataComponentDialog} />
                </DialogPopup>
            }

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={Entities.IonChromatographyIonData}
                        key={`AuditHistory${key}`}
                        childData={false}
                        rowPointer={selectedIonDatum && selectedIonDatum.ionChromatographyDataId ?
                            selectedIonDatum.ionChromatographyDataId : undefined} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    function onIonDataSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedIonDatum(e.selectedRowsData[0]);
        }
    }

    function showIonDatumInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            setSelectedIonDatum(e.row.data);
            showIonDatumInformationDialog(e.row.data);
        }
    }

    function onCloseIonDataComponentDialog() {
        getIonData();
        hideIonDataDialog();
    }

    async function getIonData() {
        await CrudFunctions.getData(`benchsheets/iciondatabybenchsheetid/${props.benchSheetId}`)
            .then(data => {
                setIonData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Ion Data");
            });
    }

    function uomCellRender(e: any) {
        if (e.data && e.data.uom && e.data.uom.name) {
            return <div>{e.data.uom.name}</div>
        }
        else {
            return (
                <div></div>
            );
        }
    }

    async function getAllData() {
        getIonData();
    }

    // show/hide

    function showIonDatumInformationDialog(ionDatum: IonChromatographyIonDatum) {
        if (ionDatum) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: ionDatum.ionChromatographyDataId,
                createTimestamp: ionDatum.createTimestamp,
                lastModifiedTimestamp: ionDatum.modifyTimestamp,
                createUser: ionDatum.createUser,
                modifyUser: ionDatum.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showIonDataDialog() {
        setIonDataDialogVisible(true);
    }

    function hideIonDataDialog() {
        setIonDataDialogVisible(false);
    }

    function showAuditInformationDialog() {
        setAuditInformationDialogVisible(true);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}